import { buildContextUsageViewModel } from './timeline/context-usage/view-model.js';
import { buildTimelineItems } from './timeline/build.js';
import { workspaceSurface } from './workspaceSurface.js';
import { canonicalContext } from './canonical.js';
import { selectCurrentComposerInputProjection } from './composer/composerUiProjectionSelector.js';
import { attachmentView } from './composer/attachments.js';
import { attachCompletedMessageCapabilityActions } from './message-action/completedMessageCapabilityActions.js';
import { normalizeRecommendationPayload } from '../adapters/chat/recommendations';
import { PRIMARY_FLOW_PAYLOAD_TIMELINE_TYPE } from 'flare-chat-core/contracts/timeline/item-types';

const STREAM_FIELDS = Object.freeze({
  agent_status: 'agentStatus', thinking_trace: 'thinkingTrace', execution_trace: 'executionTrace',
  workflow_projection: 'workflowProjection', canvas_state: 'canvasState', plan_payload: 'planPayload',
  analysis_payload: 'analysisPayload', knowledge_search: 'knowledgeSearch', knowledge_citation: 'knowledgeCitation',
  sourcing_candidates: 'sourcingCandidates', context_usage: 'contextUsage',
  delivered_artifacts: 'deliveredArtifacts',
  context_activity: 'contextActivity', context_authority: 'contextAuthority',
});

const READ_PRESENTATIONS = new Map([
  ['pending', { active: true, label: '正在加载会话', interaction: 'blocked' }],
  ['failed', { active: false, label: '会话加载失败，请重试', interaction: '' }],
]);
const READ_RETRY_POLICIES = new Map([
  [false, { label: '会话加载失败', allowed: false }],
  [true, { label: '会话加载失败，请重试', allowed: true }],
  [null, { label: '会话加载失败，请重试', allowed: true }],
]);

/** Translate the owning read/navigation receipt, never infer selection from list or stream loading. */
const workspaceReadView = (state, actions, projects) => {
  const navigation = projects.sidebarNavigation?.navigation;
  const owners = [
    { matches: Boolean(navigation?.sessionId && navigation.status === 'pending'),
      read: navigation, retry: () => projects.sidebarNavigation.openSession(navigation.projectId, navigation.sessionId) },
    { matches: Boolean(navigation?.sessionId && navigation.status === 'failed'
      && (!state.session.read?.targetSessionId || (state.session.read.status === 'failed'
        && state.session.read.targetSessionId === navigation.sessionId))),
      read: navigation, retry: () => projects.sidebarNavigation.openSession(navigation.projectId, navigation.sessionId) },
    { matches: true, read: state.session.read,
      retry: () => actions.sessionList.onSelectSession(state.session.read.targetSessionId) },
  ];
  const owner = owners.find(({ matches }) => matches);
  const read = owner.read ?? {};
  const presentation = READ_PRESENTATIONS.get(read.status);
  const recovery = READ_RETRY_POLICIES.get(read.error?.retryable ?? null);
  const failedPresentation = new Map([['failed', { label: recovery.label }]]).get(read.status);
  return {
    ...presentation, ...failedPresentation, active: presentation?.active === true,
    error: read.status === 'failed' ? read.error : null,
    operationKey: 'session_read', requestId: read.requestId ?? 0,
    targetSessionId: read.targetSessionId ?? read.sessionId ?? '', presentation: 'retain',
    onRetry: recovery.allowed ? () => Promise.resolve().then(owner.retry).catch(() => {}) : undefined,
  };
};

/** Same prepared process projection as FLARE's host; an empty array remains authoritative. */
const streamProcessView = (projections) => {
  const processPhases = Object.entries(projections).filter(([key]) => key.startsWith('user_process:'))
    .map(([, value]) => value.payload);
  return { processPhases, phase: processPhases.at(-1) };
};

const streamLifecycleView = (stream, projections) => {
  const workflow = projections.workflow_projection?.payload ?? {};
  const runState = String(workflow.progress_projection?.status ?? workflow.run_state ?? '').trim();
  const generating = ['connecting', 'streaming'].includes(stream?.status)
    && ['', 'running', 'queued'].includes(runState);
  return { runState, generating };
};

/** Adapt the public snapshot to the existing presentation builder, without another store. */
export const timelineView = (state) => {
  const stream = state.timeline.find(({ type }) => type === 'streaming');
  const projections = stream?.projections ?? {};
  const streaming = stream ? {
    content: stream.content, roundKey: stream.backendRequestId || stream.runId,
    sessionId: stream.sessionId, runId: stream.runId, backendRequestId: stream.backendRequestId,
    status: stream.status, executionProfile: stream.executionProfile,
    messageIdentityKey: stream.messageIdentityKey,
    ...streamProcessView(projections),
    ...streamLifecycleView(stream, projections),
    canonicalPayload: canonicalContext(state),
    ...Object.fromEntries(Object.entries(STREAM_FIELDS)
      .filter(([key]) => Object.hasOwn(projections, key))
      .map(([key, field]) => [field, projections[key].payload])),
  } : {};
  const timeline = buildTimelineItems({
    // Public FLARE records carry round identity in message_projection or run_id.
    // Adapt that identity for the existing presentation builder, never infer a new round.
    messages: state.timeline.filter(({ type }) => type === 'message').map((message) => ({
      ...message, round_key: message.message_projection?.round_key || message.round_key || message.run_id || '',
    })),
    streaming, executionCards: projections.execution_traces?.payload ?? [],
  }, { loading: state.composer.loading });
  // Both history and stream cross the same FLARE-to-product normalization boundary.
  const normalizedItems = timeline.items.map((item) => {
    if (item.type !== PRIMARY_FLOW_PAYLOAD_TIMELINE_TYPE) return item;
    const payload = normalizeRecommendationPayload(item.payload);
    return payload === item.payload ? item : { ...item, payload };
  });
  return normalizedItems.map((item) => item.type === 'streaming' && stream ? {
    ...item, sessionId: stream.sessionId, runId: stream.runId,
    status: stream.status, partial: stream.partial, error: stream.error,
  } : item);
};

/** @param {import('../application/capabilities/contracts').SkillAvailability & {owner?: import('../application/capabilities/contracts').SkillReadOwner} | null} skills */
export const createHostViewModel = (state, actions, projects, contextUsageFormatters, skills = null, capabilityScopeKey = '') => {
  const items = attachCompletedMessageCapabilityActions(timelineView(state),
    skills?.status === 'ready' ? skills.projection : null, state.session.sessionId);
  const workspaceSuspension = workspaceReadView(state, actions, projects);
  return {
  workspaceSuspension,
  messageCapabilities: { status: skills?.status ?? 'unavailable', error: skills?.error ?? null,
    projection: skills?.status === 'ready' ? skills.projection : null, scopeKey: capabilityScopeKey,
    readCurrent: () => skills?.owner?.isCurrent() ? skills.owner.getSnapshot() : null },
  sessionList: {
    ...actions.sessionList, ...projects,
    collectionScopeKey: projects.projection?.scope.projectId ?? '',
    sessionContinuationKey: state.sessions.window.nextCursor,
    workspaceSurface: workspaceSurface(state, projects),
    sortOrder: state.sessions.sortOrder,
    sessions: state.sessions.items, activeSessionId: state.session.sessionId,
    runningSessionIds: state.sessions.runningSessionIds,
    loadingSessionId: workspaceSuspension.active ? workspaceSuspension.targetSessionId : '',
    loading: state.sessions.loading, loadingMore: state.sessions.loadingMore,
    hasMore: state.sessions.window.hasMore, error: state.sessions.error ?? projects.error,
    bootStatus: state.lifecycle.status === 'ready' ? 'ready' : state.lifecycle.status === 'failed' ? 'failed' : 'pending',
  },
  session: { ...actions.session, context: canonicalContext(state), instanceProfile: state.session.instanceProfile },
  timeline: { ...actions.timeline, items, loading: state.composer.loading, messageWindow: state.session.messageWindow },
  composer: { ...actions.composer, inputProjection: selectCurrentComposerInputProjection({
    sessionId: state.session.sessionId, sessionContext: canonicalContext(state),
    run: state.session.detail?.active_run ?? state.session.detail?.latest_run,
  }), value: state.composer.inputValue, loading: state.composer.loading,
    submitting: state.composer.submitting, error: state.composer.error,
    hintText: state.session.error?.message ?? '', fileList: state.composer.fileList.map(attachmentView),
  },
  toolbar: actions.toolbar,
  contextUsage: contextUsageFormatters ? buildContextUsageViewModel({ timelineItems: items, sessionDetail: state.session.detail,
    contextUsage: state.timeline.find(({ type }) => type === 'streaming')?.projections?.context_usage?.payload }, contextUsageFormatters) : null,
  };
};
